import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, MapPin, Cookie } from 'lucide-react';
import BrandLogo from '@/components/branding/BrandLogo';
import { useConsent } from '@/lib/consent';

const COL_FORMATS = [
  { to: '/ad-formats/zero-click', label: 'Zero-Click' },
  { to: '/ad-formats/domain-redirect', label: 'Domain Redirect' },
  { to: '/ad-formats/pop', label: 'Pop Traffic' },
  { to: '/ad-formats/push', label: 'Push Traffic' },
  { to: '/ad-formats/in-page-push', label: 'In-Page Push' },
];
const COL_VERTICALS = [
  { to: '/verticals/igaming', label: 'iGaming' },
  { to: '/verticals/finance', label: 'Finance & Trading' },
  { to: '/verticals/ecommerce', label: 'E-commerce' },
  { to: '/verticals/sweepstakes', label: 'Sweepstakes' },
  { to: '/verticals/lead-generation', label: 'Lead Generation' },
  { to: '/verticals/software-utilities', label: 'Software & Utilities' },
  { to: '/verticals/vpn', label: 'VPN' },
];
const COL_FEATURES = [
  { to: '/features/ai-traffic-intelligence', label: 'AI Traffic Intelligence' },
  { to: '/features/source-level-control', label: 'Source-Level Control' },
  { to: '/features/fraud-filtering', label: 'Fraud Filtering' },
  { to: '/features/top-up-model', label: 'Top-Up Model' },
  { to: '/features/smart-optimization', label: 'Smart Optimization' },
];
const COL_LEADGEN = [
  { to: '/lead-generation/real-estate', label: 'Real Estate' },
  { to: '/lead-generation/credit-cards', label: 'Credit Cards' },
  { to: '/lead-generation/insurance', label: 'Insurance' },
  { to: '/lead-generation', label: 'All lead-gen verticals' },
];
const COL_COMPANY = [
  { to: '/about', label: 'About' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/blog', label: 'Blog' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
  { to: '/apply', label: 'Apply as Advertiser' },
];
const COL_LEGAL = [
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms-and-conditions', label: 'Terms & Conditions' },
  { to: '/acceptable-use-policy', label: 'Acceptable Use' },
  { to: '/cookie-policy', label: 'Cookie Policy' },
];

export default function Footer() {
  const { openManager } = useConsent();
  return (
    <footer className="bg-[#050B17] border-t border-white/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center group">
              <BrandLogo variant="footer" size={34} />
            </Link>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
              Premium performance traffic flows for serious advertisers. Curated formats, source-level controls, and hands-on launch support.
            </p>
            <p className="mt-3 text-[12.5px] text-slate-500 leading-relaxed max-w-sm" data-testid="footer-entity-blurb">
              <span className="text-slate-300 font-medium">Ads Dashbound</span> is a performance traffic platform for advertisers, agencies, and media buyers using curated traffic formats, source-level controls, AI-assisted routing signals, fraud filtering, and top-up based launch support.
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm text-slate-400">
              <a href="mailto:contact@dashbound.media" className="inline-flex items-center gap-2 hover:text-[#22D3EE] transition-colors">
                <Mail className="h-4 w-4" /> contact@dashbound.media
              </a>
              <a href="https://t.me/dashbound_media" target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#22D3EE] transition-colors">
                <Send className="h-4 w-4" /> @dashbound_media
              </a>
              <div className="flex items-start gap-2 text-slate-400">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <span className="block text-slate-200 font-medium">DASHBOUND MEDIA - FZCO</span>
                  Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai, United Arab Emirates
                </span>
              </div>
            </div>
          </div>
          <FooterCol title="Ad Formats" items={COL_FORMATS} />
          <FooterCol title="Verticals" items={COL_VERTICALS} />
          <FooterCol title="Lead Gen" items={COL_LEADGEN} />
          <FooterCol title="Features" items={COL_FEATURES} />
          <FooterCol title="Company" items={COL_COMPANY} />
        </div>

        {/* Payment methods strip */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shrink-0">
              Accepted payments
            </span>
            <div className="flex flex-wrap items-center gap-3" data-testid="footer-payment-methods">
              <PayBadge testId="pay-visa" ariaLabel="Visa">
                <VisaLogo />
              </PayBadge>
              <PayBadge testId="pay-mastercard" ariaLabel="Mastercard">
                <MastercardLogo />
              </PayBadge>
              <PayBadge href="https://capitalist.net/" testId="pay-capitalist" ariaLabel="Capitalist">
                <CapitalistLogo />
              </PayBadge>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} <span className="text-slate-300">DASHBOUND MEDIA - FZCO</span>. All rights reserved. Operating Ads Dashbound on Dashbound.media.
          </p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            {COL_LEGAL.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-slate-500 hover:text-[#22D3EE] transition-colors" data-testid={`footer-legal-${l.to.slice(1)}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={openManager}
                data-testid="footer-manage-cookies"
                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#22D3EE] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#22D3EE]/40 rounded"
              >
                <Cookie className="h-3.5 w-3.5" /> Manage cookies
              </button>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <h4 className="font-display text-sm font-semibold text-white mb-3">{title}</h4>
      <ul className="space-y-2">
        {items.map((i) => (
          <li key={i.to}>
            <Link to={i.to} className="text-sm text-slate-400 hover:text-[#22D3EE] transition-colors" data-testid={`footer-link-${i.to.slice(1).replace(/\//g, '-')}`}>
              {i.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PayBadge({ children, href, testId, ariaLabel }) {
  const className =
    'inline-flex items-center justify-center h-10 min-w-[64px] px-3 rounded-lg bg-white border border-slate-200/40 shadow-[0_4px_12px_rgba(0,0,0,0.20)] transition-transform duration-200 hover:scale-[1.03]';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" aria-label={ariaLabel} data-testid={testId} className={className}>
        {children}
      </a>
    );
  }
  return (
    <span aria-label={ariaLabel} data-testid={testId} className={className}>
      {children}
    </span>
  );
}

/* ---------- Inline payment logos (clean, brand-recognizable) ---------- */

function VisaLogo() {
  return (
    <svg width="46" height="16" viewBox="0 0 46 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M19.6 0.5L17.4 14h-3.5l2.2-13.5h3.5zM34.4 9.2l1.8-5 1.1 5h-2.9zM38.4 14h3.2L38.7 0.5h-3c-0.7 0-1.3 0.4-1.5 1L29 14h3.7l0.7-2.1h4.5l0.5 2.1zM26.5 9.6c0-3.5-4.8-3.7-4.8-5.3 0-0.5 0.5-1 1.5-1.1 0.5-0.1 1.9-0.1 3.5 0.7l0.6-3C26.6 0.5 25.5 0.2 24.1 0.2c-3.4 0-5.8 1.8-5.8 4.4 0 1.9 1.7 3 3 3.6 1.3 0.7 1.8 1.1 1.8 1.7 0 0.9-1.1 1.3-2.1 1.3-1.8 0-2.8-0.5-3.6-0.9l-0.6 3.1c0.8 0.4 2.4 0.7 4.1 0.7 3.6 0 6-1.8 6-4.5zM14.5 0.5L8.7 14H5L2.2 3.1c-0.2-0.7-0.3-1-0.9-1.3C0.4 1.4 -0.5 1 -1.4 0.8L-1.3 0.5h6c0.8 0 1.5 0.5 1.7 1.4l1.5 7.7L11.7 0.5h2.8z"
        fill="#1A1F71"
        transform="translate(2 1)"
      />
    </svg>
  );
}

function MastercardLogo() {
  return (
    <svg width="42" height="26" viewBox="0 0 42 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="14" cy="13" r="11" fill="#EB001B" />
      <circle cx="28" cy="13" r="11" fill="#F79E1B" />
      <path
        d="M21 4.5c2.4 2.1 3.9 5.1 3.9 8.5s-1.5 6.4-3.9 8.5c-2.4-2.1-3.9-5.1-3.9-8.5s1.5-6.4 3.9-8.5z"
        fill="#FF5F00"
      />
    </svg>
  );
}

function CapitalistLogo() {
  return (
    <svg width="86" height="18" viewBox="0 0 86 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="cap-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0E3A8A" />
          <stop offset="100%" stopColor="#1E66D8" />
        </linearGradient>
      </defs>
      <g>
        <circle cx="9" cy="9" r="8.5" fill="url(#cap-g)" />
        <path d="M9 4.5 L11.6 8.6 L9 12.7 L6.4 8.6 Z" fill="#FFFFFF" />
        <circle cx="9" cy="9" r="1.6" fill="#0E3A8A" />
      </g>
      <text
        x="22"
        y="13"
        fontFamily="'Space Grotesk', system-ui, sans-serif"
        fontWeight="700"
        fontSize="12"
        letterSpacing="-0.02em"
        fill="#0E3A8A"
      >
        Capitalist
      </text>
    </svg>
  );
}
